import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Modal, Box } from "../../../components/ui";
import { campusApi } from "../campus-api";
import { Campus, CreateCampusDto, UpdateCampusDto } from "../types";
import { modalStyle } from "../../../style/common";

interface CampusFormModalProps {
  open: boolean;
  onClose: () => void;
  campus?: Campus | null;
}

export default function CampusFormModal({ open, onClose, campus }: CampusFormModalProps) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (campus) {
      setName(campus.name);
      setLocation(campus.location);
    } else {
      setName("");
      setLocation("");
    }
  }, [campus, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !location.trim()) {
      toast.warning("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      if (campus) {
        const updateData: UpdateCampusDto = { name, location };
        await campusApi.updateCampus(campus.id, updateData);
        toast.success("Campus updated successfully");
      } else {
        const createData: CreateCampusDto = { name, location };
        await campusApi.createCampus(createData);
        toast.success("Campus created successfully");
      }
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to save campus");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} open={open}>
      <Box sx={modalStyle}>
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {campus ? "Edit Campus" : "Add Campus"}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Campus Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter campus name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter location"
                required
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Saving..." : campus ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      </Box>
    </Modal>
  );
}

